import { useState } from 'react';

import { Button, FieldLabel, showSnackbar, TextInput } from 'fluxo-ui';

import type { ComponentEvent } from 'fluxo-ui';

interface LiveQuizJoinFormProps {
    initialJoinCode?: string;
    onJoin: (joinCode: string, name: string) => Promise<void>;
}

const joinCodeLength = 6;

const LiveQuizJoinForm: React.FC<LiveQuizJoinFormProps> = ({ initialJoinCode = '', onJoin }) => {
    const [joinCode, setJoinCode] = useState(initialJoinCode.toUpperCase());
    const [name, setName] = useState('');
    const [joining, setJoining] = useState(false);

    const handleJoin = async () => {
        if (joinCode.trim().length !== joinCodeLength) {
            showSnackbar(`Join codes are ${joinCodeLength} characters`, 'Check the code', { type: 'error' });
            return;
        }
        if (!name.trim()) {
            showSnackbar('Pick a nickname so others can see you', 'Name required', { type: 'error' });
            return;
        }

        setJoining(true);
        try {
            await onJoin(joinCode.trim(), name.trim());
        } catch (error) {
            showSnackbar(error instanceof Error ? error.message : 'Could not join the quiz', 'Join failed', {
                type: 'error',
            });
        } finally {
            setJoining(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 grid place-items-center px-4 py-10">
            <div className="w-full max-w-sm">
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Join the quiz</h1>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Enter the code shown on the host's screen.
                    </p>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-5">
                    <FieldLabel label="Join code" required>
                        <TextInput
                            value={joinCode}
                            onChange={(e: ComponentEvent<string>) =>
                                setJoinCode(e.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, joinCodeLength))
                            }
                            placeholder="ABC123"
                            disabled={joining}
                            className="font-mono tracking-[0.3em] text-center text-lg"
                        />
                    </FieldLabel>

                    <FieldLabel label="Your nickname" required>
                        <TextInput
                            value={name}
                            onChange={(e: ComponentEvent<string>) => setName(e.value.slice(0, 20))}
                            placeholder="Anything you like"
                            disabled={joining}
                        />
                    </FieldLabel>

                    <Button
                        label="Join"
                        variant="primary"
                        size="lg"
                        className="w-full"
                        isLoading={joining}
                        disabled={joining}
                        onClick={handleJoin}
                    />
                </div>
            </div>
        </div>
    );
};

export default LiveQuizJoinForm;
